import type { SupportingPagesByTopicSnapshotData } from './api-types';
import type { TopicOwnershipDomain } from './review-page-model';
import {
  FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT,
  filterCompetingEntityRowsFromRivalColumns,
  getFocalAnchorTopicSlugs,
  isComparisonLikeUrlForTopicAnchors,
  type DomainTopicUrlRow,
  type TopicSlugLabel
} from './topic-supporting-pages';

/**
 * Live-path anchors (used when no snapshot exists). Site-wide anchor prevalence
 * is a whole-domain measurement the client does not hold, so ambient head-term
 * demotion (findings §15) only happens in the snapshot build; the slug-specific
 * co-anchor rescue (findings §24) works from the focal URL alone and applies here.
 */
export function resolveFocalAnchorTopics(
  analysis: { topic?: { topicSignals?: unknown } } | null | undefined,
  focalUrl?: string
): { slugs: Set<string>; labels: string[]; topics: TopicSlugLabel[] } {
  const isComparisonLike = isComparisonLikeUrlForTopicAnchors(focalUrl || '');
  return getFocalAnchorTopicSlugs(analysis, {
    ...(isComparisonLike ? { minRelativeWeight: FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT } : {}),
    ...(focalUrl ? { focalUrl } : {})
  });
}

/**
 * The Supporting pages snapshot's domain blocks, as URL Detail holds them.
 *
 * FW-ENTITY-01: the API applies the entity filter on read too; it is repeated
 * here so a snapshot served by an older backend is cleaned the same way. A
 * rival block left with no rows is dropped; your own block always stays.
 */
export function topicOwnershipDomainsFromSnapshot(
  data: Pick<SupportingPagesByTopicSnapshotData, 'domains' | 'focalUrl'>,
  pageUrl: string
): TopicOwnershipDomain[] {
  return filterCompetingEntityRowsFromRivalColumns(
    data.domains || [],
    data.focalUrl || (pageUrl || '').trim()
  )
    .filter((block) => block.isYourSite || (block.rows?.length ?? 0) > 0)
    .map((block) => ({
      domain: block.domain,
      isYourSite: block.isYourSite,
      rows: block.rows as DomainTopicUrlRow[]
    }));
}
